import { useCallback, useEffect, useRef, useState } from "react";

export interface AsyncState<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  reload: () => Promise<void>;
  setData: (updater: T | ((prev: T | null) => T | null)) => void;
}

/** Carrega dados ao montar e sempre que `deps` mudar. Cancela respostas atrasadas. */
export function useAsync<T>(fn: (signal: AbortSignal) => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const gen = useRef(0);
  const ctrl = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    ctrl.current?.abort();
    const c = new AbortController();
    ctrl.current = c;
    const my = ++gen.current;
    setLoading(true);
    try {
      const r = await fn(c.signal);
      if (my === gen.current) {
        setData(r);
        setError(null);
      }
    } catch (e) {
      if (my === gen.current && !(e instanceof DOMException && e.name === "AbortError")) setError(e);
    } finally {
      if (my === gen.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    // deps mudaram (ex.: outro :id na mesma rota): limpa o dado anterior para nunca renderizar o registro antigo sob a URL nova
    setData(null);
    setError(null);
    void load();
    return () => ctrl.current?.abort();
  }, [load]);

  const setDataStable = useCallback((u: T | ((prev: T | null) => T | null)) => {
    setData((prev) => (typeof u === "function" ? (u as (p: T | null) => T | null)(prev) : u));
  }, []);

  return { data, error, loading, reload: load, setData: setDataStable };
}

/** Repete `fn` a cada `intervalMs` enquanto `enabled`. Sem sobreposição. */
export function usePolling(fn: () => Promise<void> | void, intervalMs: number, enabled: boolean) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let running = false;
    const tick = async () => {
      if (running || stopped || document.visibilityState === "hidden") return;
      running = true;
      try {
        await fnRef.current();
      } finally {
        running = false;
      }
    };
    const id = window.setInterval(tick, intervalMs);
    // ao voltar para a aba, atualiza na hora em vez de esperar o próximo tick
    const onVis = () => {
      if (document.visibilityState === "visible") void tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [intervalMs, enabled]);
}

/** Estado de submissão de formulário: pending + erro. */
export function useSubmit<A extends unknown[], R>(fn: (...args: A) => Promise<R>) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const run = useCallback(
    async (...args: A): Promise<R | undefined> => {
      setPending(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (e) {
        setError(e);
        return undefined;
      } finally {
        setPending(false);
      }
    },
    [fn],
  );
  return { run, pending, error, setError };
}
